import { animate, state, style, transition, trigger } from '@angular/animations';
import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import type { AnimationItem } from 'lottie-web';
import { fromEvent } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import buttonCompletedState from '../../../../assets/animations/button_completed_state.json';
import buttonFailedState from '../../../../assets/animations/button_failed_state.json';
import buttonLoadingState from '../../../../assets/animations/button_loading_state.json';

/**
 * List of classes to add to UIKitButton instances based on host attributes to
 * style as different variants.
 */
const BUTTON_HOST_ATTRIBUTES = [
  'uikit-button',
  'inverted',
  'delete',
  'big',
  'small',
  'context',
  'context-no-fill',
  'min-width',
  'max-width',
  'icon-button',
  'menu-button',
  'survey',
];

export type ThemePalette = 'primary' | 'secondary' | 'ghost' | 'white' | 'transparent' | 'error';
/**
 * UIKit design button.
 * @deprecated Use FormaButton from Forma instead, which should be used for all new development. UIKitButton should only be used for legacy support until all buttons are migrated to FormaButton, at which point UIKitButton can be removed.
 */
@Component({
  selector: 'button[uikit-button]',
  exportAs: 'uiButton',
  templateUrl: 'button.html',
  styleUrls: ['button.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeAnimation', [
      state('in', style({ opacity: 1 })),
      state('out', style({ opacity: 0 })),
      transition('in => out', [animate(200)]),
      transition('out => in', [animate(500)]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UIKitButton implements OnDestroy, AfterViewInit, OnChanges {
  elementRef = inject(ElementRef);
  private _focusMonitor = inject(FocusMonitor);
  private cd = inject(ChangeDetectorRef);

  @ViewChild('bodyMovin') bodyMovinContainer: ElementRef;
  private _color: ThemePalette | string;
  @Input() icon: string;
  @Input() loading: boolean;
  @Input() buttonActionSuccess = true;

  @Input() get color(): ThemePalette | string {
    return this._color;
  }
  set color(value: ThemePalette | string) {
    const el = this.elementRef.nativeElement;
    if (!this._isIconButton()) {
      const colorPalette = value || 'primary';
      if (colorPalette !== this._color) {
        if (this._color) {
          el.classList.remove(`uikit-${this._color}`);
        }
        if (colorPalette) {
          el.classList.add(`uikit-${colorPalette}`);
        }

        this._color = colorPalette;
      }
    } else {
      el.style.cssText = 'background-color: ' + value?.valueOf();
    }
  }
  private _disabled = false;

  @HostBinding('attr.disabled')
  get isDisabled() {
    return this.disabled || null;
  }

  @Input() iconPosition: string;
  @Input('disabled')
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
  }

  @HostBinding('attr.selected') public _selected: boolean;

  @Input('selected')
  get selected() {
    return this._selected;
  }
  set selected(value: BooleanInput) {
    this._selected = coerceBooleanProperty(value);
  }

  @Output() animationCompleted = new EventEmitter<boolean>();

  @HostBinding('class.unclickable')
  showAnimationContainer: boolean;
  private buttonAnimation: AnimationItem;

  constructor() {
    const elementRef = this.elementRef;

    this.color = this.color;
    // For each of the variant selectors that is prevent in the button's host
    // attributes, add the correct corresponding class.
    for (const attr of BUTTON_HOST_ATTRIBUTES) {
      if (this._hasHostAttributes(attr)) {
        (elementRef.nativeElement as HTMLElement).classList.add(attr);
      }
    }
    this._focusMonitor.monitor(this.elementRef.nativeElement, true);
  }

  ngAfterViewInit(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    if (this.icon && this.icon.length > 0) {
      el.classList.add('icon');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { loading, iconPosition } = changes;
    const el = this.elementRef.nativeElement as HTMLElement;

    if (iconPosition) {
      if (iconPosition.currentValue === 'right') {
        el.classList.add('icon-right');
      } else {
        el.classList.remove('icon-right');
      }
    }

    if (loading) {
      if (loading.currentValue) {
        if (this.bodyMovinContainer != null) {
          this.showAnimationContainer = loading.currentValue;
          this.loadAnimation(buttonLoadingState, true);
        }
      } else if (this.buttonAnimation) {
        fromEvent(this.buttonAnimation, 'loopComplete')
          .pipe(
            switchMap(() => {
              this.buttonAnimation.destroy();
              return this.loadAnimation(
                this.buttonActionSuccess ? buttonCompletedState : buttonFailedState,
                false,
              ).then((anim) => fromEvent(anim, 'complete'));
            }),
          )
          .subscribe(() => {
            this.buttonAnimation.destroy();
            this.showAnimationContainer = loading.currentValue;
            this.cd.detectChanges();
            this.animationCompleted.emit(true);
          });
        this.buttonAnimation.setSpeed(5);
      } else if (loading.previousValue) {
        this.showAnimationContainer = loading.currentValue;
        this.animationCompleted.emit(true);
      }
    }
  }

  private async loadAnimation(animationData: unknown, loop: boolean): Promise<AnimationItem> {
    const { default: bodymovin } = await import('lottie-web');
    this.buttonAnimation = bodymovin.loadAnimation({
      container: this.bodyMovinContainer.nativeElement,
      renderer: 'svg',
      loop,
      autoplay: true,
      animationData,
    });
    return this.buttonAnimation;
  }

  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this.elementRef.nativeElement);
  }

  /** Focuses the button. */
  focus(): void {
    this._getHostElement().focus();
  }

  private _getHostElement() {
    return this.elementRef.nativeElement;
  }

  /** Gets whether the button has one of the given attributes. */
  private _hasHostAttributes(...attributes: string[]) {
    return attributes.some((attribute) => this._getHostElement().hasAttribute(attribute));
  }

  private _isIconButton(): boolean {
    return this._hasHostAttributes('icon-button');
  }
}

/**
 * UIKit design button.
 * @deprecated Use FormaButton from Forma instead, which should be used for all new development. UIKitButton should only be used for legacy support until all buttons are migrated to FormaButton, at which point UIKitButton can be removed.
 */
@Component({
  selector: 'a[uikit-button], a[uikit-icon-button]',
  exportAs: 'uiButton, uiAnchor',
  templateUrl: 'button.html',
  styleUrls: ['button.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.disabled]': 'disabled || null',
  },
  inputs: ['disabled'],
  standalone: false,
})
export class UIKitAnchor extends UIKitButton {
  constructor() {
    const focusMonitor = inject(FocusMonitor);
    const elementRef = inject(ElementRef);
    const cd = inject(ChangeDetectorRef);

    super();
  }

  @HostListener('click', ['$event'])
  _haltDisabledEvents(event: Event) {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
